import React, { useState, useEffect } from 'react';
import config from '../config';
import './ViewHealthData.css';  

// Helper to format numbers with comma separators
const formatNumber = (value) => {
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  if (typeof value === 'string' && value !== '' && !isNaN(Number(value))) {
    return Number(value).toLocaleString();
  }
  return value;
};

const ViewHealthData = () => {
  // State for authentication and member data
  const [member_id, setMemberId] = useState(() => localStorage.getItem('member_id'));
  const [user_id, setUserId] = useState(() => localStorage.getItem('user_id'));
  const [auth_token, setAuthToken] = useState(() => localStorage.getItem('auth_token'));
  const [assessment_date, setAssessmentDate] = useState(() => localStorage.getItem('assessment_date')); 
  const [authError, setAuthError] = useState(null);
  
  // State for member selection dropdown
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  
  // State for menu selection
  const [selectedMenu, setSelectedMenu] = useState('body_summary');
  
  // State for data
  const [data, setData] = useState([]);
  const [exerciseData, setExerciseData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State for pagination
  const [pagination, setPagination] = useState({
    curPage: 1,
    perPage: 10,
    sort: 'datetime',
    itemsReceived: 0,
    itemsTotal: 0,
    pageTotal: 0,
    nextPage: null,
    prevPage: null
  });

  // State for AI comment popup
  const [showAIPopup, setShowAIPopup] = useState(false);
  const [aiComment, setAIComment] = useState('');

  // Field constants for AI prompt inclusion
  const SLEEP_FIELDS = [
    'sleep_date_string',
    'breaths_avg_per_min_int',
    'snoring_events_count_int',
    'saturation_minimum_percentage_int',
    'time_to_fall_asleep_seconds_int',
    'hr_avg_bpm_int',
    'hrv_avg_sdnn_float',
    'sleep_duration_seconds_int',
    'sleep_rem_duration_seconds_int',
    'light_sleep_duration_seconds_int',
    'deep_sleep_duration_seconds_int',
  ];

  const BODY_METRICS_FIELDS = [ 
    'datetime',
    'oxygenation_saturation_avg_percentage_int',
    'hr_resting_bpm_int',
    'hrv_avg_sdnn_float',
  ];

  // Menu items configuration
  const menuItems = [
    { key: 'body_summary', label: 'Body Summary', endpoint: 'body_summary' },
    { key: 'physical_summary', label: 'Physical Summary', endpoint: 'physical_summary' },
    { key: 'sleep', label: 'Sleep', endpoint: 'sleep' }
  ];

  // Fetch list of members for dropdown (for testing)
  useEffect(() => {
    if (!member_id) {
    setIsLoadingMembers(true);
    fetch(config.healthdata_apiUrl + '/all_members', {
      headers: {
        'Authorization': `Bearer ${auth_token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setMembers(data);
        } else {
          console.error('Members API did not return an array:', data);
          setMembers([]);
        }
        setIsLoadingMembers(false);
      })
      .catch((err) => {
        console.error('Error fetching members:', err);
        setMembers([]);
        setIsLoadingMembers(false);
      });
    }
  }, [auth_token, member_id]);

  // When dropdown changes, update member_id and fetch data
  useEffect(() => {
    if (selectedMember) {
      setMemberId(selectedMember);
    }
  }, [selectedMember]);

  // Persist auth data to localStorage when it changes
  useEffect(() => {
    if (member_id && user_id && auth_token) {
      localStorage.setItem('member_id', member_id);
      localStorage.setItem('user_id', user_id);
      localStorage.setItem('auth_token', auth_token); 
    }
  }, [member_id, user_id, auth_token, assessment_date]);

  // Define allowed origins for postMessage
  const allowedOrigins = [
    'https://www.infinity-mc-hms.com',
    'https://infinity-mc-healthplan-edit-dot-infinity-medical-concier-724b0.lm.r.appspot.com',
    'http://localhost:3000',
    'http://localhost:5000',
    'https://localhost:3000',
    'https://localhost:5000'
  ];

  // Add message event listener to receive auth data
  useEffect(() => {
    console.log('Setting up message listener for ViewHealthData...');
    console.log('Parent window exists:', !!window.parent);
    console.log('Is same origin:', window.parent === window);
    
    const handleMessage = (event) => {
      console.log('Received message:', {
        ...event,
        data: event.data?.target ? { target: event.data.target } : event.data
      });
      
      // Ignore MetaMask and webpack messages
      if (event.data?.target === 'metamask-contentscript' || 
          event.data?.target === 'metamask-inpage' ||
          event.data?.type === 'webpackWarnings') {
        return;
      }

      //console.log('Processing message from origin:', event.origin);
      
      // Verify the origin of the message
      if (!allowedOrigins.includes(event.origin)) {
        console.log('Message origin not in allowed list:', event.origin);
        console.log('Allowed origins:', allowedOrigins);
        return;
      }

      // Verify the message structure
      if (event.data && event.data.type === 'AUTH_DATA') {
        const { member_id, user_id, auth_token, assessment_date: incomingAssessmentDate } = event.data;
        console.log('Auth data received:', { 
          member_id, 
          user_id, 
          auth_token: auth_token ? '***' : undefined
        });
        
        if (member_id && user_id && auth_token) {
          console.log('Setting auth data...');
          setMemberId(member_id);
          setUserId(user_id);
          setAuthToken(auth_token);
          setAssessmentDate(incomingAssessmentDate);
          setAuthError(null);
          console.log('Auth data successfully set');
        } else {
          console.error('Missing required auth data:', { 
            hasMemberId: !!member_id, 
            hasUserId: !!user_id, 
            hasAuthToken: !!auth_token 
          });
          setAuthError('Missing required authentication data. Please ensure you are properly logged in.');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Request auth data from parent only if we don't have it
    /*
    if (!member_id || !user_id || !auth_token) {
      console.log('Requesting auth data from parent...');
      allowedOrigins.forEach(origin => {
        try {
          window.parent.postMessage({ type: 'REQUEST_AUTH' }, origin);
          console.log('Auth request sent to origin:', origin);
        } catch (error) {
          console.error('Error sending auth request to origin:', origin, error);
        }
      });
    } else {
      console.log('Already have auth data, skipping request');
      setIsAuthenticating(false);
    }
    */
    return () => {
      console.log('Cleaning up message listener...');
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Fetch exercise data for a specific member
  const fetchExerciseData = async (memberId) => {
    if (!memberId || !auth_token) {
      return;
    }

    try {
      const url = `https://xuar-e6vh-cmcf.e2.xano.io/api:2K9hwDeK/healthdata_physical_event/no_json/${memberId}`;
      console.log('Fetching exercise data from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${auth_token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        console.error('Exercise data API response error:', {
          status: response.status,
          statusText: response.statusText,
          url
        });
        return;
      }
      
      const exerciseData = await response.json();
      console.log('Received exercise data:', exerciseData);
      
      // Group exercise data by date using activity_start_datetime_string
      const groupedByDate = {};
      exerciseData.forEach(exercise => {
        if (exercise.activity_start_datetime_string) {
          const dateKey = formatDateOnly(exercise.activity_start_datetime_string);
          if (!groupedByDate[dateKey]) {
            groupedByDate[dateKey] = [];
          }
          groupedByDate[dateKey].push(exercise);
        }
      });
      
      setExerciseData(groupedByDate);
    } catch (err) {
      console.error('Error fetching exercise data:', err);
    }
  };

  // Fetch data based on selected menu and pagination
  const fetchData = async () => {
    // Don't fetch if we're still authenticating or don't have auth data
    if (!member_id || !auth_token) {
      console.log('Waiting for authentication before fetching data...');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const selectedMenuItem = menuItems.find(item => item.key === selectedMenu);
      if (!selectedMenuItem) {
        throw new Error('Invalid menu selection');
      }

      let url;
      if (selectedMenu === 'sleep' || selectedMenu === 'body_summary' || selectedMenu === 'physical_summary') {
        // Use perPage and offset for these
        const offset = (pagination.curPage - 1) * pagination.perPage;
        url = `${config.healthdata_apiUrl}/healthdata/${selectedMenuItem.endpoint}/${member_id}?perPage=${pagination.perPage}&offset=${offset}&sort=${pagination.sort}`;
      } else {
        // fallback (should not be used)
        const offset = (pagination.curPage - 1) * pagination.perPage;
        url = `${config.healthdata_apiUrl}/healthdata/${selectedMenuItem.endpoint}/${member_id}?curPage=${pagination.curPage}&perPage=${pagination.perPage}&offset=${offset}&sort=${pagination.sort}`;
      }
      
      console.log('Fetching data from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${auth_token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API response error:', {
          status: response.status,
          statusText: response.statusText,
          errorText,
          url
        });
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      if (result.length === 0) {
        setError('No data found for this member.');
        return;
      }
      
      console.log('Received data:', result);
      
      // Fetch exercise data if physical_summary is selected
      if (selectedMenu === 'physical_summary') {
        await fetchExerciseData(member_id);
      }
      
      if (selectedMenu === 'sleep' || selectedMenu === 'body_summary' || selectedMenu === 'physical_summary') {
        // New response: { data: [...], meta: { curPage, perPage, total: [{total: N}] } }
        setData(result.data || []);
        const itemsTotal = result.meta?.total?.[0]?.total || 0;
        const currentPage = (result.meta?.curPage ?? 0) + 1; // convert 0-based to 1-based
        const perPage = result.meta?.perPage ?? pagination.perPage;
        const pageTotal = Math.ceil(itemsTotal / perPage);
        
        // Calculate nextPage and prevPage
        const nextPage = currentPage < pageTotal ? currentPage + 1 : null;
        const prevPage = currentPage > 1 ? currentPage - 1 : null;
        
        setPagination(prev => ({
          ...prev,
          perPage: perPage,
          itemsTotal: itemsTotal,
          pageTotal: pageTotal,
          nextPage: nextPage,
          prevPage: prevPage,
          itemsReceived: result.data?.length || 0
          // Don't override curPage - keep the requested page
        }));
      } else {
        setData(result.items || []);
        setPagination(prev => ({
          ...prev,
          itemsReceived: result.itemsReceived || 0,
          itemsTotal: result.itemsTotal || 0,
          pageTotal: result.pageTotal || 0,
          nextPage: result.nextPage,
          prevPage: result.prevPage
        }));
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when menu selection or pagination changes, but only if authenticated
  useEffect(() => {
    if (member_id && auth_token) {
      fetchData();
    }
  }, [selectedMenu, pagination.curPage, pagination.perPage, pagination.sort, member_id, auth_token]);

  // Add effect to log state changes
  useEffect(() => {
    console.log('Auth state changed:', {
      hasMemberId: !!member_id,
      hasUserId: !!user_id,
      hasAuthToken: !!auth_token,
      isLoading: loading,
      hasError: !!error,
      hasAuthError: !!authError,
    });
  }, [member_id, user_id, auth_token, loading, error, authError]);

  // Handle pagination changes
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      curPage: newPage
    }));
  };

  const handlePerPageChange = (newPerPage) => {
    setPagination(prev => ({
      ...prev,
      perPage: newPerPage,
      curPage: 1 // Reset to first page when perPage changes
    }));
  };

  const handleSortChange = (newSort) => {
    setPagination(prev => ({
      ...prev,
      sort: newSort
    }));
  };

  // Format timestamp to readable date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Format timestamp to date only (for duplicate detection)
  const formatDateOnly = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return date.toDateString(); // Returns date in format "Mon Jan 01 2024"
  };

  // Check if a date has duplicates in the current data set
  const hasDuplicateDate = (timestamp, currentIndex) => {
    if (!timestamp) return false;
    const dateOnly = formatDateOnly(timestamp);
    
    return data.some((item, index) => {
      if (index === currentIndex) return false; // Don't compare with self
      return formatDateOnly(item.datetime) === dateOnly;
    });
  };

  // Format duration from seconds to hours and minutes
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Helper for temperature display
  const formatTemperature = (avg, min, max, delta) => {
    const f = v => {
      if (v === null || v === undefined || v === '' || v === 0 || v === 'N/A' || v === '0') {
        return 'N/A';
      }
      const num = parseFloat(v);
      if (isNaN(num)) return 'N/A';
      return `${num.toFixed(1)}°C`;
    };
    
    const formattedAvg = f(avg);
    const formattedMin = f(min);
    const formattedMax = f(max);
    const formattedDelta = f(delta);
    
    // If all are N/A, return 'N/A'
    if (formattedAvg === 'N/A' && formattedMin === 'N/A' && formattedMax === 'N/A' && formattedDelta === 'N/A') {
      return 'N/A';
    }
    
    return `${formattedAvg} / ${formattedMin} / ${formattedMax} (Δ${formattedDelta})`;
  };
  // Helper for continuity
  const formatContinuity = (score, rating) => {
    if (!score && !rating) return 'N/A';
    return `${score || 'N/A'} / ${rating || 'N/A'}`;
  };
  // Helper for HRV
  const formatHRV = (rmssd, sdnn) => {
    return `${Math.round(rmssd) || 'N/A'} / ${Math.round(sdnn) || 'N/A'}`;
  };
  // Helper for HR
  const formatHR = (min, max, rest) => {
    const allNA = [min, max, rest].every(v => v === null || v === undefined || v === '' || v === 0 || v === 'N/A' || v === '0');
    if (allNA) return 'N/A';
    return `${min || 'N/A'} / ${max || 'N/A'} / ${rest || 'N/A'}`;
  };
  // Helper for saturation
  const formatSaturation = (min, avg, max) => {
    const allNA = [min, max, avg].every(v => v === null || v === undefined || v === '' || v === 0 || v === 'N/A' || v === '0');
    if (allNA) return 'N/A';
    return `${min || 'N/A'} / ${avg || 'N/A'} / ${max || 'N/A'}`;
  };
  // Helper for blood pressure
  const formatBloodPressure = (systolic, diastolic) => {
    if (!systolic && !diastolic) return 'N/A';
    return `${systolic || 'N/A'} / ${diastolic || 'N/A'}`;
  };
  // Helper for breaths
  const formatBreaths = (min, avg, max) => {
    const allNA = [min, avg, max].every(v => v === null || v === undefined || v === '' || v === 0 || v === 'N/A' || v === '0');
    if (allNA) return 'N/A';
    return `${min || 'N/A'} / ${avg || 'N/A'} / ${max || 'N/A'}`;
  };
  // Helper for snoring duration
  const formatSnoringDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}m${sec ? ' ' + sec + 's' : ''}`;
  };



  // Helper for step comparison
  const getStepComparison = (steps, avgSteps) => {
    if (!steps || !avgSteps || steps === 'N/A' || avgSteps === 'N/A') {
      return { arrow: '', title: '' };
    }
    
    const stepsNum = parseFloat(steps);
    const avgStepsNum = Math.round(parseFloat(avgSteps));
    
    if (isNaN(stepsNum) || isNaN(avgStepsNum)) {
      return { arrow: '', title: '' };
    }
    
    if (stepsNum > avgStepsNum) {
      return { 
        arrow: '⬆️', 
        title: `Day's steps (${stepsNum.toLocaleString()}) are higher than the previous 7-day average of ${avgStepsNum.toLocaleString()}`
      };
    } else if (stepsNum < avgStepsNum) {
      return { 
        arrow: '⬇️', 
        title: `Day's steps (${stepsNum.toLocaleString()}) are lower than the previous 7-day average of ${avgStepsNum.toLocaleString()}`
      };
    } else {
      return { 
        arrow: '➡️', 
        title: `Day's steps (${stepsNum.toLocaleString()}) equal the previous 7-day average of ${avgStepsNum.toLocaleString()}`
      };
    }
  };

  // Helper to format exercise data for display
  const formatExerciseData = (dateKey) => {
    const exercises = exerciseData[dateKey];
    if (!exercises || exercises.length === 0) {
      return 'N/A';
    }
    
    return exercises.map(exercise => {
      const duration = exercise.activity_duration_seconds_int;
      const calories = exercise.calories_expenditure_kcal_float;
      const activityName = exercise.activity_type_name_string || 'Unknown Activity';
      
      // Format duration from seconds to minutes
      const durationMinutes = duration ? Math.round(duration / 60) : 0;
      
      // Format calories - only show if not 0 or null
      const caloriesFormatted = calories && calories !== 0 ? Math.round(calories) : null;
      
      // Build the exercise string
      let exerciseString = `${activityName} (${durationMinutes}min`;
      if (caloriesFormatted !== null) {
        exerciseString += `; ${caloriesFormatted}kcal`;
      }
      exerciseString += ')';
      
      return exerciseString;
    }).join('; ');
  };

  // Helper for energy expenditure calculation
  const calculateEnergyExpenditure = (calorieExpenditure, basalMetabolicRate) => {
    // Check if either component is N/A, null, undefined, empty, or 0 (including string "0")
    if (!calorieExpenditure || !basalMetabolicRate || 
        calorieExpenditure === 'N/A' || basalMetabolicRate === 'N/A' ||
        calorieExpenditure === 0 || basalMetabolicRate === 0 ||
        calorieExpenditure === '0' || basalMetabolicRate === '0' ||
        calorieExpenditure === null || basalMetabolicRate === null ||
        calorieExpenditure === undefined || basalMetabolicRate === undefined ||
        calorieExpenditure === '' || basalMetabolicRate === '') {
      return { value: 'N/A', colorClass: '' };
    }
    
    const expenditure = parseFloat(calorieExpenditure);
    const bmr = parseFloat(basalMetabolicRate);
    
    if (isNaN(expenditure) || isNaN(bmr)) {
      return { value: 'N/A', colorClass: '' };
    }
    
    const energyExpenditure = Math.round(expenditure - bmr);
    const formattedValue = formatNumber(energyExpenditure);
    
    // Color coding: green for positive, red for negative
    const colorClass = energyExpenditure > 0 ? 'row-green' : 
                      energyExpenditure < 0 ? 'row-red' : '';
    
    return { value: formattedValue, colorClass };
  };

  // Helper to check if all values in a column are 'N/A'
  const isAllNA = (data, fieldKey, formatter = null) => {
    if (!data || data.length === 0) return true;
    
    // If a formatter function is provided, use it to format each value
    if (formatter) {
      return data.every(item => {
        const formattedValue = formatter(item);
        return formattedValue === 'N/A';
      });
    }
    
    // Otherwise, check the raw field values
    return data.every(item => {
      const value = item[fieldKey];
      return value === null || value === undefined || value === '' || value === 0 || value === 'N/A' || value === '0';
    });
  };

  // Helper to get present fields for body_summary
  const getBodyMetricsPresentFields = () => {
    // Returns array of { key, label } for present fields, always including Date
    if (!data || data.length === 0) return [];
    const fieldMap = {
      weight_kg_float: 'Weight (kg)',
      height_cm_int: 'Height (cm)',
      bmi_float: 'BMI',
      blood_glucose_avg_mg_per_dL_int: 'Blood Glucose (mg/dL)', 
      oxygenation_vo2max_mL_per_min_per_kg_int: 'VO2max (mL/min/kg)',
      menstruation_cycle_day_int: 'Menstruation Cycle Day',
      menstruation_current_phase_string: 'Menstruation Phase',
    };
    const present = [{ key: 'datetime', label: 'Date' }];
    Object.entries(fieldMap).forEach(([key, label]) => {
      if (!isAllNA(data, key)) {
        present.push({ key, label });
      }
    });
    
    // Check composite columns
    if (!isAllNA(data, null, (item) => {
      const hrvVals = [item.hrv_avg_rmssd_float, item.hrv_avg_sdnn_float];
      const hasAnyHRV = hrvVals.some(v => v !== null && v !== undefined && v !== '' && v !== 0 && v !== 'N/A');
      if (hasAnyHRV) {
        const formattedHRV = formatHRV(item.hrv_avg_rmssd_float, item.hrv_avg_sdnn_float);
        if (!/^(0|N\/A)\s*\/\s*(0|N\/A)$/.test(formattedHRV)) {
          return formattedHRV;
        }
      }
      return 'N/A';
    })) {
      present.push({ key: 'hrv_composite', label: 'HRV (RMSSD/SDNN)' });
    }
    
    if (!isAllNA(data, null, (item) => formatHR(item.hr_minimum_bpm_int, item.hr_maximum_bpm_int, item.hr_resting_bpm_int))) {
      present.push({ key: 'hr_composite', label: 'HR (min/max/rest)' });
    }
    
    if (!isAllNA(data, null, (item) => formatSaturation(item.oxygenation_saturation_minimum_percentage_int, item.oxygenation_saturation_avg_percentage_int, item.oxygenation_saturation_maximum_percentage_int))) {
      present.push({ key: 'saturation_composite', label: 'Saturation (min/avg/max)' });
    }
    
    if (!isAllNA(data, null, (item) => {
      const tempVals = [
        item.temperature_avg_object_temperature_celsius_float,
        item.temperature_minimum_object_temperature_celsius_float,
        item.temperature_maximum_object_temperature_celsius_float,
        item.temperature_delta_object_temperature_celsius_float
      ];
      const hasAnyTemp = tempVals.some(v => v !== null && v !== undefined && v !== '' && v !== 0 && v !== 'N/A');
      if (hasAnyTemp) {
        const formattedTemp = formatTemperature(item.temperature_avg_object_temperature_celsius_float, item.temperature_minimum_object_temperature_celsius_float, item.temperature_maximum_object_temperature_celsius_float, item.temperature_delta_object_temperature_celsius_float);
        if (!/^N\/A\s*\/\s*N\/A\s*\/\s*N\/A\s*\(ΔN\/A\)$/.test(formattedTemp)) {
          return formattedTemp;
        }
      }
      return 'N/A';
    })) {
      present.push({ key: 'temperature_composite', label: 'Temperature (avg/min/max/delta)' });
    }
    
    if (!isAllNA(data, null, (item) => formatBloodPressure(item.blood_pressure_systolic_mmHg_int, item.blood_pressure_diastolic_mmHg_int))) {
      present.push({ key: 'blood_pressure_composite', label: 'Blood Pressure (systolic/diastolic)' });
    }
    
    return present;
  };

  // Helper to get present fields for physical_summary
  const getPhysicalSummaryPresentFields = () => {
    if (!data || data.length === 0) return [];
    const fieldMap = {
      activity_active_seconds_int: 'Active Time',
      activity_rest_seconds_int: 'Rest Time',
      activity_low_intensity_seconds_int: 'Low Intensity Time',
      activity_moderate_intensity_seconds_int: 'Moderate Intensity Time',
      activity_vigorous_intensity_seconds_int: 'Vigorous Intensity Time',
      activity_inactivity_seconds_int: 'Inactivity Time',
      activity_continuous_inactive_periods_int: 'Continuous Inactive Periods',
      calories_net_intake_kcal_float: 'Net Calorie Intake (kcal)',
      calories_expenditure_kcal_float: 'Calorie Expenditure (kcal)',
      calories_basal_metabolic_rate_kcal_float: 'Basal Metabolic Rate (kcal)',
      steps_int: 'Steps',
      walked_distance_meters_float: 'Walked Distance (m)',
      traveled_distance_meters_float: 'Traveled Distance (m)',
      floors_climbed_float: 'Floors Climbed',
      swimming_num_strokes_float: 'Swim Strokes',
      swimming_num_laps_int: 'Swim Laps',
      swimming_total_distance_meters_float: 'Swim Distance (m)',
      active_steps_int: 'Active Steps',
    };
    const present = [{ key: 'datetime', label: 'Date' }];
    Object.entries(fieldMap).forEach(([key, label]) => {
      if (!isAllNA(data, key)) {
        present.push({ key, label });
      }
    });
    
    // Add Energy Expenditure column if both Calorie Expenditure and Basal Metabolic Rate are present
    const hasCalorieExpenditure = !isAllNA(data, 'calories_expenditure_kcal_float');
    const hasBasalMetabolicRate = !isAllNA(data, 'calories_basal_metabolic_rate_kcal_float');
    
    if (hasCalorieExpenditure && hasBasalMetabolicRate) {
      // Insert Energy Expenditure after Basal Metabolic Rate
      const bmrIndex = present.findIndex(field => field.key === 'calories_basal_metabolic_rate_kcal_float');
      if (bmrIndex !== -1) {
        present.splice(bmrIndex + 1, 0, { key: 'energy_expenditure_kcal_float', label: 'Energy Expenditure (kcal)' });
      }
    }
    
    // Add Exercise Details column
    present.push({ key: 'exercise_details', label: 'Exercise Details' });
    
    return present;
  };

  // Helper to get present fields for sleep
  const getSleepPresentFields = () => {
    if (!data || data.length === 0) return [];
    
    const fieldMap = {
      sleep_duration_seconds_int: 'Sleep Duration',
      time_in_bed_seconds_int: 'Time in Bed',
      light_sleep_duration_seconds_int: 'Light Sleep',
      rem_sleep_duration_seconds_int: 'REM Sleep',
      deep_sleep_duration_seconds_int: 'Deep Sleep',
      time_to_fall_asleep_seconds_int: 'Time to Fall Asleep',
      time_awake_during_sleep_seconds_int: 'Time Awake',
      snoring_events_count_int: 'Snoring Events',
      snoring_duration_total_seconds_int: 'Snoring Duration',
      sleep_quality_rating_1_5_score_int: 'Sleep Quality (1-5)',
      sleep_efficiency_1_100_score_int: 'Sleep Efficiency (1-100)',
      sleep_goal_seconds_int: 'Sleep Goal',
      sleep_continuity_1_5_score_int: 'Sleep Continuity (score/rating)',
    };
    
    const present = [{ key: 'datetime', label: 'Date' }];
    
    // Check individual fields
    Object.entries(fieldMap).forEach(([key, label]) => {
      if (!isAllNA(data, key)) {
        present.push({ key, label });
      }
    });
    
    // Check composite fields
    if (!isAllNA(data, null, (item) => formatBreaths(item.breaths_minimum_per_min_int, item.breaths_avg_per_min_int, item.breaths_maximum_per_min_int))) {
      present.push({ key: 'breaths_composite', label: 'Breaths (min/avg/max)' });
    }
    
    if (!isAllNA(data, null, (item) => formatSaturation(item.saturation_minimum_percentage_int, item.saturation_avg_percentage_int, item.saturation_maximum_percentage_int))) {
      present.push({ key: 'saturation_composite', label: 'Saturation (min/avg/max)' });
    }
    
    if (!isAllNA(data, null, (item) => formatHR(item.hr_minimum_bpm_int, item.hr_maximum_bpm_int, item.hr_resting_bpm_int))) {
      present.push({ key: 'hr_composite', label: 'HR (min/max/rest)' });
    }
    
    if (!isAllNA(data, null, (item) => formatHRV(item.hrv_avg_rmssd_float, item.hrv_avg_sdnn_float))) {
      present.push({ key: 'hrv_composite', label: 'HRV (RMSSD/SDNN)' });
    }
    
    if (!isAllNA(data, null, (item) => formatTemperature(item.temperature_avg_object_temperature_celsius_float, item.temperature_minimum_object_temperature_celsius_float, item.temperature_maximum_object_temperature_celsius_float, item.temperature_delta_object_temperature_celsius_float))) {
      present.push({ key: 'temperature_composite', label: 'Temperature (avg/min/max/delta)' });
    }
    
    return present;
  };

  // Render table headers and data based on selected menu
  const renderTable = () => {
    if (!data || data.length === 0) {
      return <div className="no-data">No data available</div>;
    }

    // Function to determine if a header should have the data-included-in-ai-prompt class
    const shouldIncludeInAIPrompt = (header) => {
      if (selectedMenu === 'sleep') {
        // Map header labels to field names for sleep
        const headerToFieldMap = {
          'Date': 'sleep_date_string',
          'Breaths (min/avg/max)': 'breaths_avg_per_min_int',
          'Snoring Events': 'snoring_events_count_int',
          'Saturation (min/avg/max)': 'saturation_minimum_percentage_int',
          'Time to Fall Asleep': 'time_to_fall_asleep_seconds_int',
          'HR (min/max/rest)': 'hr_avg_bpm_int',
          'HRV (RMSSD/SDNN)': 'hrv_avg_sdnn_float',
          'Sleep Duration': 'sleep_duration_seconds_int', 
          'Time in Bed': 'sleep_rem_duration_seconds_int', 
          'Light Sleep': 'light_sleep_duration_seconds_int',
          'Deep Sleep': 'deep_sleep_duration_seconds_int',
        };
        return SLEEP_FIELDS.includes(headerToFieldMap[header]);
      } else if (selectedMenu === 'body_summary') {
        // Map header labels to field names for body_summary
        const headerToFieldMap = {
          'Date': 'datetime',
          'Saturation (min/avg/max)': 'oxygenation_saturation_avg_percentage_int',
          'HR (min/max/rest)': 'hr_resting_bpm_int',
          'HRV (RMSSD/SDNN)': 'hrv_avg_sdnn_float',
          'Blood Pressure (systolic/diastolic)': 'blood_pressure_composite',
        };
        return BODY_METRICS_FIELDS.includes(headerToFieldMap[header]);
      }
      return false;
    };

    const getHeaders = () => {
      switch (selectedMenu) {
        case 'body_summary': {
          const presentFields = getBodyMetricsPresentFields();
          const headers = presentFields.map(field => field.label);
          headers.push('AI Score');
          return headers;
        }
                case 'physical_summary': {
          const presentFields = getPhysicalSummaryPresentFields();
          const headers = presentFields.map(field => field.label);
          headers.push('AI Score');
          return headers;
        }
        case 'sleep': {
          const presentFields = getSleepPresentFields();
          const headers = presentFields.map(field => field.label);
          headers.push('AI Score');
          return headers;
        }
        default:
          return [];
      }
    };

    const getRowData = (item) => {
      switch (selectedMenu) {
        case 'body_summary': {
          // Dynamically build row based on present fields
          const presentFields = getBodyMetricsPresentFields();
          const rowData = presentFields.map(({ key, label }) => {
            if (key === 'datetime') return formatDateOnly(item.datetime);
            if (key === 'hrv_composite') {
              // Show composite HRV for table cell (no label)
              const hrvVals = [item.hrv_avg_rmssd_float, item.hrv_avg_sdnn_float];
              const hasAnyHRV = hrvVals.some(v => v !== null && v !== undefined && v !== '' && v !== 0 && v !== 'N/A');
              if (hasAnyHRV) {
                const formattedHRV = formatHRV(item.hrv_avg_rmssd_float, item.hrv_avg_sdnn_float);
                // Only include if not '0 / 0', 'N/A / N/A', or any combo of 0/N/A
                if (!/^(0|N\/A)\s*\/\s*(0|N\/A)$/.test(formattedHRV)) {
                  return formattedHRV;
                }
              }
              return 'N/A';
            }
            if (key === 'hr_composite') {
              return formatHR(item.hr_minimum_bpm_int, item.hr_maximum_bpm_int, item.hr_resting_bpm_int);
            }
            if (key === 'saturation_composite') {
              return formatSaturation(item.oxygenation_saturation_minimum_percentage_int, item.oxygenation_saturation_avg_percentage_int, item.oxygenation_saturation_maximum_percentage_int);
            }
            if (key === 'temperature_composite') {
              const tempVals = [
                item.temperature_avg_object_temperature_celsius_float,
                item.temperature_minimum_object_temperature_celsius_float,
                item.temperature_maximum_object_temperature_celsius_float,
                item.temperature_delta_object_temperature_celsius_float
              ];
              const hasAnyTemp = tempVals.some(v => v !== null && v !== undefined && v !== '' && v !== 0 && v !== 'N/A');
              if (hasAnyTemp) {
                const formattedTemp = formatTemperature(item.temperature_avg_object_temperature_celsius_float, item.temperature_minimum_object_temperature_celsius_float, item.temperature_maximum_object_temperature_celsius_float, item.temperature_delta_object_temperature_celsius_float);
                if (!/^N\/A\s*\/\s*N\/A\s*\/\s*N\/A\s*\(ΔN\/A\)$/.test(formattedTemp)) {
                  return formattedTemp;
                }
              }
              return 'N/A';
            }
            if (key === 'blood_pressure_composite') {
              return formatBloodPressure(item.blood_pressure_systolic_mmHg_int, item.blood_pressure_diastolic_mmHg_int);
            }
            // Show N/A for missing
            return (item[key] !== null && item[key] !== undefined && item[key] !== '' && item[key] !== 0 && item[key] !== 'N/A' && item[key] !== '0') ? formatNumber(item[key]) : 'N/A';
          });
          // Add AI Score at the end
          rowData.push(item.ai_score || 'N/A');
          return rowData;
        }
        case 'physical_summary': {
          const presentFields = getPhysicalSummaryPresentFields();
          const rowData = presentFields.map(({ key, label }) => {
            if (key === 'datetime') return formatDateOnly(item.datetime);
            // Format durations for seconds fields
            if (key.endsWith('_seconds_int')) {
              return formatDuration(item[key]);
            }
            // Special handling for steps with comparison
            if (key === 'steps_int') {
              const stepsValue = (item[key] !== null && item[key] !== undefined && item[key] !== '' && item[key] !== 0 && item[key] !== 'N/A' && item[key] !== '0') ? formatNumber(item[key]) : 'N/A';
              const stepComparison = getStepComparison(item[key], item.steps_int_avg_7d);
              if (stepComparison.arrow) {
                return `${stepsValue} ${stepComparison.arrow}`;
              }
              return stepsValue;
            }
            // Special handling for energy expenditure calculation
            if (key === 'energy_expenditure_kcal_float') {
              const energyExpenditure = calculateEnergyExpenditure(
                item.calories_expenditure_kcal_float, 
                item.calories_basal_metabolic_rate_kcal_float
              );
              return energyExpenditure.value;
            }
            // Special handling for exercise details
            if (key === 'exercise_details') {
              const dateKey = formatDateOnly(item.datetime);
              return formatExerciseData(dateKey);
            }
            // Show N/A for missing
            return (item[key] !== null && item[key] !== undefined && item[key] !== '' && item[key] !== 0 && item[key] !== 'N/A' && item[key] !== '0') ? formatNumber(item[key]) : 'N/A';
          });
          // Add AI Score at the end
          rowData.push(item.ai_score || 'N/A');
          return rowData;
        }
        case 'sleep': {
          const presentFields = getSleepPresentFields();
          const rowData = presentFields.map(({ key, label }) => {
            if (key === 'datetime') return formatDateOnly(item.datetime);
            if (key === 'breaths_composite') {
              return formatBreaths(item.breaths_minimum_per_min_int, item.breaths_avg_per_min_int, item.breaths_maximum_per_min_int);
            }
            if (key === 'saturation_composite') {
              return formatSaturation(item.saturation_minimum_percentage_int, item.saturation_avg_percentage_int, item.saturation_maximum_percentage_int);
            }
            if (key === 'hr_composite') {
              return formatHR(item.hr_minimum_bpm_int, item.hr_maximum_bpm_int, item.hr_resting_bpm_int);
            }
            if (key === 'hrv_composite') {
              return formatHRV(item.hrv_avg_rmssd_float, item.hrv_avg_sdnn_float);
            }
            if (key === 'temperature_composite') {
              return formatTemperature(item.temperature_avg_object_temperature_celsius_float, item.temperature_minimum_object_temperature_celsius_float, item.temperature_maximum_object_temperature_celsius_float, item.temperature_delta_object_temperature_celsius_float);
            }
            if (key === 'sleep_continuity_1_5_score_int') {
              return formatContinuity(item.sleep_continuity_1_5_score_int, item.sleep_continuity_1_5_rating_int);
            }
            if (key === 'snoring_duration_total_seconds_int') {
              return formatSnoringDuration(item.snoring_duration_total_seconds_int);
            }
            if (key.endsWith('_seconds_int')) {
              return formatDuration(item[key]);
            }
            return (item[key] !== null && item[key] !== undefined && item[key] !== '' && item[key] !== 0 && item[key] !== 'N/A' && item[key] !== '0') ? formatNumber(item[key]) : 'N/A';
          });
          rowData.push(item.ai_score || 'N/A');
          return rowData;
        }
        default:
          return [];
      }
    };

    const headers = getHeaders();

    return (
      <div className="table-container">
        <table className="health-data-table">
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th 
                  key={index}
                  className={shouldIncludeInAIPrompt(header) ? 'data-included-in-ai-prompt' : ''}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => {
              // Determine row color based on AI score
              const aiScore = item.ai_score;
              let rowColorClass = '';
              
              // Use AI score for color coding
              if (aiScore !== null && aiScore !== undefined && aiScore !== '' && aiScore !== 'N/A' && aiScore !== '0') {
                const score = parseFloat(aiScore);
                if (!isNaN(score) && score > 0) {
                  if (score >= 0 && score <= 4) {
                    rowColorClass = 'row-green';
                  } else if (score >= 5 && score <= 6) {
                    rowColorClass = 'row-orange';
                  } else if (score >= 7) {
                    rowColorClass = 'row-red';
                  }
                }
              }
              
              return (
                <tr 
                  key={item.id || index} 
                  className={rowColorClass}
                >
                  {getRowData(item).map((cell, cellIndex) => {
                    // Check if this is the date cell (first column) and if it has duplicates
                    const isDateCell = cellIndex === 0;
                    const isDuplicateDate = isDateCell && hasDuplicateDate(item.datetime, index);
                    // Gray if N/A or 0 (except for date cell)
                    const isNA = cell === 'N/A' || (cell === 0 && !isDateCell) || (cell === '0' && !isDateCell);
       
                    // Check if this is the AI Score column
                    const isAIScore = cellIndex === getRowData(item).length - 1;
                    const aiComment = isAIScore ? item.ai_comment : null;
                    
                    const handleAIScoreClick = () => {
                      if (isAIScore && aiComment) {
                        setAIComment(aiComment);
                        setShowAIPopup(true);
                      }
                    };
                    
                    // Check if this is the Energy Expenditure column
                    const isEnergyExpenditure = selectedMenu === 'physical_summary' && 
                      cellIndex > 0 && 
                      getHeaders()[cellIndex] === 'Energy Expenditure (kcal)';
                    
                    // Get energy expenditure color class if applicable
                    let energyExpenditureColorClass = '';
                    if (isEnergyExpenditure) {
                      const energyExpenditure = calculateEnergyExpenditure(
                        item.calories_expenditure_kcal_float, 
                        item.calories_basal_metabolic_rate_kcal_float
                      );
                      energyExpenditureColorClass = energyExpenditure.colorClass;
                    }
                    
                    return (
                      <td 
                        key={cellIndex}
                        className={[
                          isDuplicateDate ? 'duplicate-date' : '',
                          (isNA ) ? 'na-cell' : '',
                          isAIScore ? 'ai-score-cell' : '',
                          selectedMenu === 'physical_summary' && 
                          cellIndex > 0 && 
                          getHeaders()[cellIndex] === 'Steps' && 
                          item.steps_int && 
                          item.steps_int_avg_7d ? 
                          'steps-comparison-cell' : '',
                          energyExpenditureColorClass
                        ].filter(Boolean).join(' ')}
                        onClick={isAIScore ? handleAIScoreClick : undefined}
                        title={
                          selectedMenu === 'physical_summary' && 
                          cellIndex > 0 && 
                          getHeaders()[cellIndex] === 'Steps' && 
                          item.steps_int && 
                          item.steps_int_avg_7d ? 
                          getStepComparison(item.steps_int, item.steps_int_avg_7d).title : 
                          undefined
                        }
                      >
                        {cell}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Render pagination controls
  const renderPagination = () => {
    const totalPages = pagination.pageTotal || 1;
    const totalItems = pagination.itemsTotal || 0;
    const startIdx = ((pagination.curPage - 1) * pagination.perPage) + 1;
    const endIdx = Math.min(pagination.curPage * pagination.perPage, totalItems);
    
    return (
      <div className="pagination-controls">
        <div className="pagination-info">
          <span>
            Showing {totalItems === 0 ? 0 : startIdx} to {endIdx} of {totalItems} entries
          </span>
        </div>
        
        <div className="pagination-settings">
          <label>
            Per page:
            <select
              value={pagination.perPage}
              onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          
          <label>
            Sort by:
            <select
              value={pagination.sort}
              onChange={(e) => handleSortChange(e.target.value)}
            >
              <option value="datetime">Date</option>
              <option value="created_at">Created At</option>
            </select>
          </label>
        </div>
        
        <div className="pagination-buttons">
          <button
            onClick={() => handlePageChange(pagination.curPage - 1)}
            disabled={pagination.curPage <= 1}
            className="pagination-button"
          >
            Previous
          </button>
          
          <span className="page-info">
            Page {pagination.curPage} of {totalPages}
          </span>
          
          <button
            onClick={() => handlePageChange(pagination.curPage + 1)}
            disabled={!pagination.nextPage}
            className="pagination-button"
          >
            Next
          </button>
        </div>
      </div>
    );
  };
 
 

  // Show auth error if authentication failed
  if (authError) {
    return (
      <div className="view-health-data-container">
        <div className="auth-error">
          <h2>Authentication Error</h2>
          <p>{authError}</p>
          <p>Please contact support if this issue persists.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="view-health-data-container">
      {!member_id && (
        <div className="header"> 
          {/* Member selection dropdown for testing */}
          <div style={{ margin: '16px 0' }}>
            <label htmlFor="member-select" style={{ fontWeight: 500, marginRight: 8 }}>Select Member (testing):</label>
            <select
              id="member-select"
              value={selectedMember || member_id || ''}
              onChange={e => setSelectedMember(e.target.value)}
              style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid #ccc', minWidth: 120 }}
              disabled={isLoadingMembers}
            >
              <option value="">-- Select --</option>
              {(Array.isArray(members) ? members : []).map((m, idx) => {
                // Try to support both possible member id keys
                const id = m.Member_ID  || m.id || '';
                return (
                  <option key={id || idx} value={id}>{id}</option>
                );
              })}
            </select>
          </div>
          <p>Member ID: {member_id || 'Loading...'}</p>
        </div>
      )}

      {/* Menu Navigation */}
      <div className="menu-navigation">
        {menuItems.map((item) => (
          <button
            key={item.key}
            className={`menu-item ${selectedMenu === item.key ? 'active' : ''}`}
            onClick={() => setSelectedMenu(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="content-area">
        {loading && (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading data...</p>
          </div>
        )}

        {error && (
          <div className="error">
            <p>Error: {error}</p>
            <button onClick={fetchData} className="retry-button">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {renderTable()}
            {renderPagination()}
          </>
        )}
      </div>

      {/* AI Comment Popup */}
      {showAIPopup && (
        <div className="ai-popup-overlay" onClick={() => setShowAIPopup(false)}>
          <div className="ai-popup" onClick={(e) => e.stopPropagation()}>
            <div className="ai-popup-header">
              <h3>AI Analysis</h3>
              <button 
                className="ai-popup-close" 
                onClick={() => setShowAIPopup(false)}
              >
                ×
              </button>
            </div>
            <div className="ai-popup-content">
              <p>{aiComment}</p>
            </div>
          </div>
        </div>
      )}
 
    </div>
  );
};

export default ViewHealthData;
